"use client"

import * as React from "react"
import { useRouter } from "next/navigation"
import { Star } from "lucide-react"
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command"
import { useCommandStore } from "@/store/command-store"
import { useWatchlistStore } from "@/store/watchlist-store"
import { useTokenIcons } from "@/hooks/use-token-icons"
import { useVenueMarkets } from "@/hooks/use-venue-markets"
import { VenueBadges } from "@/components/ui/venue-badges"
import { cn } from "@/lib/utils"

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface MarketEntry {
  price: number
  change: number
  quoteVolume: number
  /** Stored from metaAndAssetCtxs REST snapshot; used by allMids WS to recalculate live change */
  prevDayPx: number
}

function formatPrice(price: number): string {
  if (price >= 1000) return `$${price.toLocaleString("en-US", { maximumFractionDigits: 2 })}`
  if (price >= 1) return `$${price.toFixed(2)}`
  if (price >= 0.01) return `$${price.toFixed(4)}`
  return `$${price.toFixed(6)}`
}

function formatVolume(vol: number): string {
  if (vol >= 1e9) return `$${(vol / 1e9).toFixed(2)}B`
  if (vol >= 1e6) return `$${(vol / 1e6).toFixed(2)}M`
  if (vol >= 1e3) return `$${(vol / 1e3).toFixed(2)}K`
  return `$${vol.toFixed(2)}`
}

/** DiceBear identicon URL — unique, deterministic, SVG-based */
function identiconUrl(seed: string): string {
  return `https://api.dicebear.com/9.x/identicon/svg?seed=${encodeURIComponent(seed)}&size=28`
}

// ── HL DEX (xyz builder) asset metadata ──────────────────────────────────────

const LOGO_DEV_TOKEN = "pk_fr4838fe02d7a3ec4a224c"
const TV_CDN = "https://s3-symbol-logo.tradingview.com"

type RwaLogoCfg =
  | { type: "logodev"; domain: string }
  | { type: "tradingview"; path: string }
  | { type: "avatar"; text: string; bg: string; fg?: string }

// Logo configs for HL DEX assets — covers the full xyz universe + known extras
const RWA_CMD_LOGOS: Record<string, RwaLogoCfg> = {
  // Stocks — logodev
  AAPL:     { type: "logodev", domain: "apple.com" },
  AMD:      { type: "logodev", domain: "amd.com" },
  AMZN:     { type: "logodev", domain: "amazon.com" },
  BABA:     { type: "logodev", domain: "alibaba.com" },
  BX:       { type: "logodev", domain: "blackstone.com" },
  COIN:     { type: "logodev", domain: "coinbase.com" },
  COST:     { type: "logodev", domain: "costco.com" },
  CRCL:     { type: "logodev", domain: "circle.com" },
  CRWV:     { type: "logodev", domain: "coreweave.com" },
  DKNG:     { type: "logodev", domain: "draftkings.com" },
  GME:      { type: "logodev", domain: "gamestop.com" },
  GOOG:     { type: "logodev", domain: "google.com" },
  GOOGL:    { type: "logodev", domain: "google.com" },
  HIMS:     { type: "logodev", domain: "forhims.com" },
  HOOD:     { type: "logodev", domain: "robinhood.com" },
  HYUNDAI:  { type: "logodev", domain: "hyundai.com" },
  INTC:     { type: "logodev", domain: "intel.com" },
  KIOXIA:   { type: "logodev", domain: "kioxia.com" },
  LITE:     { type: "logodev", domain: "lumentum.com" },
  LLY:      { type: "logodev", domain: "lilly.com" },
  META:     { type: "logodev", domain: "meta.com" },
  MRVL:     { type: "logodev", domain: "marvell.com" },
  MSFT:     { type: "logodev", domain: "microsoft.com" },
  MSTR:     { type: "logodev", domain: "microstrategy.com" },
  MU:       { type: "logodev", domain: "micron.com" },
  NFLX:     { type: "logodev", domain: "netflix.com" },
  NVDA:     { type: "logodev", domain: "nvidia.com" },
  ORCL:     { type: "logodev", domain: "oracle.com" },
  PLTR:     { type: "logodev", domain: "palantir.com" },
  RIVN:     { type: "logodev", domain: "rivian.com" },
  RKLB:     { type: "logodev", domain: "rocketlabusa.com" },
  SKHX:     { type: "logodev", domain: "skhynix.com" },
  SMSN:     { type: "logodev", domain: "samsung.com" },
  SNDK:     { type: "logodev", domain: "sandisk.com" },
  SOFTBANK: { type: "logodev", domain: "softbank.com" },
  TSLA:     { type: "logodev", domain: "tesla.com" },
  TSM:      { type: "logodev", domain: "tsmc.com" },
  URNM:     { type: "avatar", text: "U",   bg: "#2d6a4f", fg: "#b7e4c7" },
  XLE:      { type: "avatar", text: "XLE", bg: "#d62828", fg: "#ffffff" },
  // Forex — country flags via tradingview
  DXY: { type: "tradingview", path: "country/US--big.svg" },
  EUR: { type: "tradingview", path: "country/EU--big.svg" },
  GBP: { type: "tradingview", path: "country/GB--big.svg" },
  JPY: { type: "tradingview", path: "country/JP--big.svg" },
  CAD: { type: "tradingview", path: "country/CA--big.svg" },
  MXN: { type: "tradingview", path: "country/MX--big.svg" },
  KRW: { type: "tradingview", path: "country/KR--big.svg" },
  AUD: { type: "tradingview", path: "country/AU--big.svg" },
  NZD: { type: "tradingview", path: "country/NZ--big.svg" },
  CHF: { type: "tradingview", path: "country/CH--big.svg" },
  // Commodities — tradingview metals/agri + avatar fallbacks for oil/gas
  XAU:       { type: "tradingview", path: "metal/gold--big.svg" },
  GOLD:      { type: "tradingview", path: "metal/gold--big.svg" },
  XAG:       { type: "tradingview", path: "metal/silver--big.svg" },
  SILVER:    { type: "tradingview", path: "metal/silver--big.svg" },
  HG:        { type: "tradingview", path: "metal/copper--big.svg" },
  COPPER:    { type: "tradingview", path: "metal/copper--big.svg" },
  PALLADIUM: { type: "tradingview", path: "metal/palladium--big.svg" },
  PLATINUM:  { type: "tradingview", path: "metal/platinum--big.svg" },
  ALUMINIUM: { type: "tradingview", path: "metal/aluminum--big.svg" },
  URANIUM:   { type: "tradingview", path: "metal/uranium--big.svg" },
  CORN:      { type: "tradingview", path: "commodity/corn--big.svg" },
  WHEAT:     { type: "tradingview", path: "commodity/wheat--big.svg" },
  CL:        { type: "avatar", text: "OIL", bg: "#1a1a2e", fg: "#cdd6df" },
  BRENTOIL:  { type: "avatar", text: "OIL", bg: "#1a1a2e", fg: "#cdd6df" },
  NATGAS:    { type: "avatar", text: "GAS", bg: "#0f3460", fg: "#a8d8ea" },
  TTF:       { type: "avatar", text: "TTF", bg: "#0f3460", fg: "#a8d8ea" },
  // Indices — tradingview
  SPX:  { type: "tradingview", path: "indices/s-and-p-500--big.svg" },
  SP500:{ type: "tradingview", path: "indices/s-and-p-500--big.svg" },
  DJI:  { type: "tradingview", path: "indices/djia--big.svg" },
  NDX:  { type: "tradingview", path: "indices/nasdaq-100--big.svg" },
  NIK:  { type: "tradingview", path: "indices/nikkei-225--big.svg" },
  JP225:{ type: "tradingview", path: "indices/nikkei-225--big.svg" },
  FTSE: { type: "tradingview", path: "indices/uk-100--big.svg" },
  DAX:  { type: "tradingview", path: "indices/dax--big.svg" },
  VIX:  { type: "avatar", text: "VIX", bg: "#6c5ce7", fg: "#ffffff" },
  XYZ100:{ type: "avatar", text: "XYZ", bg: "#00b894", fg: "#ffffff" },
  EWJ:  { type: "tradingview", path: "country/JP--big.svg" },
  EWY:  { type: "tradingview", path: "country/KR--big.svg" },
  KR200:{ type: "tradingview", path: "country/KR--big.svg" },
}

// Human-readable display names for HL DEX assets
const HL_DISPLAY_NAMES: Record<string, string> = {
  // Stocks
  AAPL: "Apple", AMD: "AMD", AMZN: "Amazon", BABA: "Alibaba", BX: "Blackstone",
  COIN: "Coinbase", COST: "Costco", CRCL: "Circle", CRWV: "CoreWeave",
  DKNG: "DraftKings", GME: "GameStop", GOOG: "Alphabet", GOOGL: "Alphabet",
  HIMS: "Hims & Hers", HOOD: "Robinhood", HYUNDAI: "Hyundai", INTC: "Intel",
  KIOXIA: "Kioxia", LITE: "Lite", LLY: "Eli Lilly", META: "Meta",
  MRVL: "Marvell", MSFT: "Microsoft", MSTR: "MicroStrategy", MU: "Micron",
  NFLX: "Netflix", NVDA: "Nvidia", ORCL: "Oracle", PLTR: "Palantir",
  RIVN: "Rivian", RKLB: "Rocket Lab", SKHX: "SK Hynix", SMSN: "Samsung",
  SNDK: "Sandisk", SOFTBANK: "SoftBank", TSLA: "Tesla", TSM: "TSMC",
  URNM: "Uranium ETF", XLE: "Energy ETF",
  // Ostium-only stocks
  ARM: "ARM Holdings", ASML: "ASML", AVGO: "Broadcom", BMNR: "BioMarin",
  CAT: "Caterpillar", CVX: "Chevron", GEV: "GE Vernova", GLXY: "Galaxy Digital",
  SBET: "SharkBet", SHEL: "Shell", SMCI: "Super Micro", XOM: "ExxonMobil",
  // Commodities
  ALUMINIUM: "Aluminium", BRENTOIL: "Brent Crude", BRENT: "Brent Crude",
  CL: "Crude Oil", COPPER: "Copper", CORN: "Corn", GOLD: "Gold",
  NATGAS: "Natural Gas", PALLADIUM: "Palladium", PLATINUM: "Platinum",
  SILVER: "Silver", TTF: "TTF Gas", URANIUM: "Uranium", WHEAT: "Wheat",
  XAU: "Gold", XAG: "Silver", XPT: "Platinum", XPD: "Palladium", HG: "Copper",
  // Forex
  DXY: "US Dollar Index", EUR: "EUR/USD", JPY: "USD/JPY",
  GBP: "GBP/USD", CAD: "USD/CAD", MXN: "USD/MXN", KRW: "USD/KRW",
  AUD: "AUD/USD", NZD: "NZD/USD", CHF: "USD/CHF",
  // Indices
  EWJ: "Japan ETF", EWY: "Korea ETF", JP225: "Nikkei 225",
  KR200: "KOSPI 200", SP500: "S&P 500", SPX: "S&P 500",
  VIX: "Volatility Index", XYZ100: "XYZ 100",
  DJI: "Dow Jones", NDX: "Nasdaq 100", NIK: "Nikkei 225",
  FTSE: "FTSE 100", DAX: "DAX", HSI: "Hang Seng",
}

// Categorisation sets — used to classify HL DEX assets (xyz:TSLA → "stocks")
const HL_STOCK_SET = new Set([
  "AAPL", "AMD", "AMZN", "BABA", "BX", "COIN", "COST", "CRCL", "CRWV",
  "DKNG", "GME", "GOOGL", "HIMS", "HOOD", "HYUNDAI", "INTC", "KIOXIA",
  "LITE", "LLY", "META", "MRVL", "MSFT", "MSTR", "MU", "NFLX", "NVDA",
  "ORCL", "PLTR", "RIVN", "RKLB", "SKHX", "SMSN", "SNDK", "SOFTBANK",
  "TSLA", "TSM", "URNM", "XLE",
])
const HL_COMMODITY_SET = new Set([
  "ALUMINIUM", "BRENTOIL", "CL", "COPPER", "CORN", "GOLD",
  "NATGAS", "PALLADIUM", "PLATINUM", "SILVER", "TTF", "URANIUM", "WHEAT",
])
const HL_FOREX_SET = new Set(["DXY", "EUR", "JPY"])
const HL_INDEX_SET = new Set([
  "EWJ", "EWY", "JP225", "KR200", "SP500", "VIX", "XYZ100",
])

/** Returns the command-menu filter tab for an HL DEX symbol, or null to skip it */
function hlDexFilterTab(sym: string): FilterTab | null {
  if (HL_STOCK_SET.has(sym)) return "Stocks"
  if (HL_COMMODITY_SET.has(sym)) return "Commodities"
  if (HL_FOREX_SET.has(sym)) return "Forex"
  if (HL_INDEX_SET.has(sym)) return "Indices"
  return null
}

/** URL category segment for navigating to a terminal page */
function hlDexUrlCategory(tab: FilterTab): string {
  const map: Record<string, string> = {
    Stocks: "stocks", Commodities: "commodities", Forex: "forex", Indices: "indices",
  }
  return map[tab] ?? "futures"
}

function RwaCmdIcon({ symbol }: { symbol: string }) {
  const cfg = RWA_CMD_LOGOS[symbol]
  const base = "w-7 h-7 rounded-full shrink-0 border border-border/20 overflow-hidden flex items-center justify-center"

  if (!cfg) {
    return (
      <div className={`${base} bg-muted text-[10px] font-bold text-muted-foreground`}>
        {symbol.slice(0, 3)}
      </div>
    )
  }
  if (cfg.type === "logodev") {
    return (
      <div className={base}>
        <img
          src={`https://img.logokit.com/${cfg.domain}?token=${LOGO_DEV_TOKEN}&size=64`}
          alt={symbol}
          className="w-full h-full object-cover"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none" }}
        />
      </div>
    )
  }
  if (cfg.type === "tradingview") {
    return (
      <div className={base}>
        <img
          src={`${TV_CDN}/${cfg.path}`}
          alt={symbol}
          className="w-full h-full object-cover"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none" }}
        />
      </div>
    )
  }
  return (
    <div
      className={`${base} text-[9px] font-bold`}
      style={{ backgroundColor: cfg.bg, color: cfg.fg ?? "#fff" }}
    >
      {cfg.text}
    </div>
  )
}

const FILTERS = ["All", "Futures", "Stocks", "Forex", "Commodities", "Indices"] as const
type FilterTab = (typeof FILTERS)[number]

// ─── Component ────────────────────────────────────────────────────────────────

interface HlDexCoin {
  symbol: string        // e.g. "TSLA"
  name: string          // e.g. "Tesla"
  tab: FilterTab        // "Stocks" | "Commodities" | "Forex" | "Indices"
  maxLeverage?: number  // from HL xyz universe
}

// Module-level market data caches — survive re-opens; 60 s TTL
const MARKET_CACHE_TTL = 60_000
let _cryptoMarketCache: { map: Map<string, MarketEntry>; ts: number } | null = null
let _hlDexMarketCache: { coins: HlDexCoin[]; priceMap: Map<string, MarketEntry>; ts: number } | null = null

export function GlobalCommand() {
  const { isOpen, setOpen, availableCoins, setAvailableCoins, setSelectedSymbol } = useCommandStore()
  const { symbols: watchlistSymbols, toggleSymbol: toggleWatchlist } = useWatchlistStore()
  const router = useRouter()
  const coinsFetchedRef = React.useRef(false)
  const hlDexFetchedRef = React.useRef(false)
  const { getIcon: getTokenIcon } = useTokenIcons()
  const { getVenuesForCoin } = useVenueMarkets()

  const [activeFilter, setActiveFilter] = React.useState<FilterTab>("All")

  // Market data (price, change, volume) keyed by HL asset name e.g. "BTC"
  const [marketMap, setMarketMap] = React.useState<Map<string, MarketEntry>>(new Map())
  // HL DEX assets (xyz builder) — stocks, commodities, forex, indices
  const [hlDexCoins, setHlDexCoins] = React.useState<HlDexCoin[]>([])
  const [hlDexPriceMap, setHlDexPriceMap] = React.useState<Map<string, MarketEntry>>(new Map())
  // ──⌘/Ctrl + K toggle ──────────────────────────────────────────────────────
  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen(!isOpen)
      }
    }
    document.addEventListener("keydown", down)
    return () => document.removeEventListener("keydown", down)
  }, [isOpen, setOpen])

  // ── 24h snapshot from HL REST (prevDayPx, volume) — cached 60 s ─────────────
  React.useEffect(() => {
    if (!isOpen) return
    // Serve from cache if fresh
    if (_cryptoMarketCache && Date.now() - _cryptoMarketCache.ts < MARKET_CACHE_TTL) {
      setMarketMap(_cryptoMarketCache.map)
      return
    }
    fetch("https://api.hyperliquid.xyz/info", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "metaAndAssetCtxs" }),
    })
      .then((r) => r.json())
      .then((data: any) => {
        const universe: { name: string }[] = data[0]?.universe ?? []
        const ctxs: any[] = data[1] ?? []
        const map = new Map<string, MarketEntry>()
        for (let i = 0; i < universe.length; i++) {
          const ctx = ctxs[i]
          if (!ctx) continue
          const midPx = parseFloat(ctx.midPx)
          if (!midPx) continue // skip inactive coins with null/zero midPx — avoids false -100% change
          const prevDayPx = parseFloat(ctx.prevDayPx) || 0
          const change = prevDayPx > 0 ? ((midPx - prevDayPx) / prevDayPx) * 100 : 0
          map.set(universe[i].name, {
            price: midPx,
            change,
            quoteVolume: parseFloat(ctx.dayNtlVlm) || 0,
            prevDayPx,
          })
        }
        _cryptoMarketCache = { map, ts: Date.now() }
        setMarketMap(map)
      })
      .catch(() => {})
  }, [isOpen])

  // ── Fetch all perpetual tokens (cached in store after first load) ──────────
  React.useEffect(() => {
    if (!isOpen || availableCoins.length > 0 || coinsFetchedRef.current) return
    coinsFetchedRef.current = true

    const fetchCoins = async () => {
      try {
        const cryptoResponse = await fetch(`/api/crypto?t=${Date.now()}`, {
          cache: "no-store",
          headers: { "Cache-Control": "no-cache" },
        })
        const cryptoResult = await cryptoResponse.json()
        let mainCoins: { symbol: string; name: string; logoUrl: string }[] = []
        if (cryptoResult.success) mainCoins = cryptoResult.data

        let tokenIconMap: Record<string, string> = {}
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "http://localhost:8000"
        try {
          const iconRes = await fetch("/api/token-icons")
          if (iconRes.ok) {
            const iconData = await iconRes.json()
            const rawIcons = iconData.icons || {}
            for (const [symbol, entry] of Object.entries(rawIcons)) {
              if (typeof entry === "string") {
                tokenIconMap[symbol] = entry
              } else if ((entry as any)?.url) {
                const e = entry as { url: string; needsProxy: boolean }
                tokenIconMap[symbol] = e.needsProxy
                  ? `${backendUrl}/api/market/proxy-image?url=${encodeURIComponent(e.url)}`
                  : e.url
              }
            }
          }
        } catch { /* icons optional */ }

        const proxyUrl = (url: string) =>
          url ? `${backendUrl}/api/market/proxy-image?url=${encodeURIComponent(url)}` : ""

        // Fetch HL universe for primary coin list
        const hlResponse = await fetch("https://api.hyperliquid.xyz/info", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ type: "meta" }),
        })
        const hlData = await hlResponse.json()
        const hlUniverse: { name: string; maxLeverage?: number }[] = hlData.universe ?? []

        const allSymbols = hlUniverse
          .filter((u: any) => !u.isDelisted)
          .map((u: any) => {
            const sym = u.name
            const fallback = tokenIconMap[sym.toUpperCase()]
            const existingLogo = mainCoins.find((c: any) =>
              c.symbol === `${sym}USDT` || c.symbol === sym || c.name?.toUpperCase() === sym
            )?.logoUrl
            return {
              symbol: sym,
              name: sym,
              maxLeverage: u.maxLeverage as number | undefined,
              logoUrl: existingLogo
                ? existingLogo.includes("/proxy-image") || existingLogo.includes("coingecko")
                  ? existingLogo
                  : proxyUrl(existingLogo)
                : fallback || "",
            }
          })

        // HL coins are the primary list -- mainCoins only used for logo fallback
        const coinsMap = new Map<string, { symbol: string; name: string; logoUrl: string; maxLeverage?: number }>()
        allSymbols.forEach((coin: any) => {
          coinsMap.set(coin.symbol, coin)
        })

        const sorted = Array.from(coinsMap.values()).sort((a, b) =>
          a.symbol.localeCompare(b.symbol)
        )
        setAvailableCoins(sorted)
      } catch (error) {
        console.error("[GlobalCommand] Failed to fetch tokens:", error)
      }
    }

    fetchCoins()
  }, [isOpen, availableCoins.length, setAvailableCoins])

  // ── Fetch HL DEX (xyz builder) assets + prices — cached 60 s ────────────────
  // metaAndAssetCtxs with dex:"xyz" returns [universe, ctxs] where each entry
  // has name like "xyz:TSLA" and ctx with midPx / prevDayPx / dayNtlVlm.
  React.useEffect(() => {
    if (!isOpen) return
    // Serve from cache if fresh
    if (_hlDexMarketCache && Date.now() - _hlDexMarketCache.ts < MARKET_CACHE_TTL) {
      setHlDexCoins(_hlDexMarketCache.coins)
      setHlDexPriceMap(_hlDexMarketCache.priceMap)
      return
    }
    if (hlDexFetchedRef.current) return
    hlDexFetchedRef.current = true
    fetch("https://api.hyperliquid.xyz/info", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ type: "metaAndAssetCtxs", dex: "xyz" }),
    })
      .then((r) => r.json())
      .then((data: any[]) => {
        const universe: { name: string }[] = data[0]?.universe ?? []
        const ctxs: any[] = data[1] ?? []
        const coins: HlDexCoin[] = []
        const priceMap = new Map<string, MarketEntry>()
        universe.forEach((u, i) => {
          // Strip builder prefix: "xyz:TSLA" → "TSLA"
          const sym = u.name.includes(":") ? u.name.split(":")[1] : u.name
          const tab = hlDexFilterTab(sym)
          if (!tab) return  // skip unknowns (PURRDAT, USAR, etc.)
          const ctx = ctxs[i]
          const rawMid = parseFloat(ctx?.midPx)
          const prevDay = parseFloat(ctx?.prevDayPx) || 0
          // Fall back to prevDayPx when live mid is unavailable (e.g. DXY has null midPx)
          const midPx = !isNaN(rawMid) && rawMid > 0 ? rawMid : prevDay
          coins.push({ symbol: sym, name: HL_DISPLAY_NAMES[sym] ?? sym, tab, maxLeverage: (u as any).maxLeverage as number | undefined })
          if (midPx) {
            priceMap.set(sym, {
              price: midPx,
              change: prevDay > 0 && !isNaN(rawMid) && rawMid > 0 ? ((rawMid - prevDay) / prevDay) * 100 : 0,
              quoteVolume: parseFloat(ctx?.dayNtlVlm) || 0,
              prevDayPx: prevDay,
            })
          }
        })
        _hlDexMarketCache = { coins, priceMap, ts: Date.now() }
        hlDexFetchedRef.current = false  // allow refresh after TTL expires
        setHlDexCoins(coins)
        setHlDexPriceMap(priceMap)
      })
      .catch(() => { hlDexFetchedRef.current = false })
  }, [isOpen])

  // ── Inject Ostium-only tradfi assets (not on HL DEX) ────────────────────────
  const ostiumInjectedRef = React.useRef(false)
  React.useEffect(() => {
    if (!isOpen || hlDexCoins.length === 0 || ostiumInjectedRef.current) return
    const existing = new Set(hlDexCoins.map(c => c.symbol))

    const OSTIUM_LEV: Record<string, number> = {
      // Stocks
      GOOG: 20, ARM: 20, ASML: 20, AVGO: 20, BMNR: 20, CAT: 20, CRCL: 20,
      CVX: 20, GEV: 20, GLXY: 20, SBET: 20, SHEL: 20, SMCI: 20, TSM: 20, XOM: 20,
      // Forex
      GBP: 100, CAD: 100, MXN: 100, KRW: 50, AUD: 100, NZD: 100, CHF: 100,
      // Commodities
      XAU: 50, XAG: 20, XPT: 20, XPD: 20, CL: 20, BRENT: 20, HG: 20,
      // Indices
      SPX: 50, DJI: 50, NDX: 50, NIK: 20, FTSE: 20, DAX: 20, HSI: 20,
    }

    const ostiumOnly: HlDexCoin[] = []
    const inject = (sym: string, tab: FilterTab) => {
      if (!existing.has(sym)) ostiumOnly.push({ symbol: sym, name: HL_DISPLAY_NAMES[sym] ?? sym, tab, maxLeverage: OSTIUM_LEV[sym] })
    }
    for (const s of ["GOOG", "ARM", "ASML", "AVGO", "BMNR", "CAT", "CRCL", "CVX",
      "GEV", "GLXY", "SBET", "SHEL", "SMCI", "TSM", "XOM"]) inject(s, "Stocks")
    for (const s of ["GBP", "CAD", "MXN", "KRW", "AUD", "NZD", "CHF"]) inject(s, "Forex")
    for (const s of ["XAU", "XAG", "XPT", "XPD", "CL", "BRENT"]) inject(s, "Commodities")
    for (const s of ["SPX", "DJI", "NDX", "NIK", "FTSE", "DAX", "HSI"]) inject(s, "Indices")
    if (ostiumOnly.length > 0) {
      ostiumInjectedRef.current = true
      setHlDexCoins(prev => [...prev, ...ostiumOnly])
    }
  }, [isOpen, hlDexCoins.length])

  // Reset injection flag when menu closes
  React.useEffect(() => {
    if (!isOpen) ostiumInjectedRef.current = false
  }, [isOpen])

  // ── Fetch Ostium prices for assets not covered by HL DEX ────────────────────
  React.useEffect(() => {
    if (!isOpen) return
    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "http://localhost:8000"
    fetch(`${backendUrl}/api/exchange/rwa-markets`)
      .then(r => r.json())
      .then((data: any) => {
        if (!data?.prices) return
        const changes: Record<string, number> = data.changes ?? {}
        setHlDexPriceMap(prev => {
          const next = new Map(prev)
          for (const item of data.prices) {
            const fromSym = (item.from || "").toUpperCase()
            const toSym   = (item.to   || "").toUpperCase()
            const mid = parseFloat(item.mid)
            if (!mid) continue

            // Pairs quoted as BASE/USD: from=EUR → symbol=EUR, price=mid
            // Pairs quoted as USD/QUOTE: from=USD → symbol=toSym, price=mid
            const sym = fromSym === "USD" ? toSym : fromSym
            if (!sym || sym === "USD") continue
            if (next.has(sym)) continue  // don't overwrite HL DEX prices

            const change = changes[sym] ?? 0
            next.set(sym, { price: mid, change, quoteVolume: 0, prevDayPx: 0 })
          }
          return next
        })
      })
      .catch(() => {})
  }, [isOpen])

  const runCommand = React.useCallback(
    (command: () => unknown) => {
      setOpen(false)
      // Defer navigation until after the dialog finishes closing to avoid
      // cmdk swallowing the router event during the unmount transition.
      setTimeout(() => command(), 0)
    },
    [setOpen]
  )

  return (
    <CommandDialog
      open={isOpen}
      onOpenChange={setOpen}
      title="Token Search"
      description="Search and navigate to any token"
    >
      <CommandInput placeholder="Search tokens…" />

      {/* Category filter pills */}
      <div className="flex gap-1 px-3 py-2 border-b border-border/40">
        {FILTERS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveFilter(tab)}
            className={cn(
              "px-2.5 py-0.5 text-[11px] font-medium border transition-colors",
              activeFilter === tab
                ? "border-foreground text-foreground bg-foreground/5"
                : "border-border/50 text-muted-foreground hover:text-foreground hover:border-border"
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      <CommandList>
        <CommandEmpty>No tokens found.</CommandEmpty>

        <CommandGroup heading="Futures" className={activeFilter !== "All" && activeFilter !== "Futures" ? "hidden" : ""}>
          {availableCoins.length === 0 && (
            <CommandItem value="loading-tokens" disabled className="py-3">
              <div className="flex items-center gap-2 text-muted-foreground">
                <div className="size-3 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                <span className="text-sm">Loading tokens…</span>
              </div>
            </CommandItem>
          )}

          {availableCoins.map((coin) => {
            const resolvedIcon = getTokenIcon(coin.symbol) || coin.logoUrl || null
            const isWatchlisted = watchlistSymbols.includes(coin.symbol)
            const market = marketMap.get(coin.symbol)
            const isPositive = (market?.change ?? 0) >= 0

            return (
              <CommandItem
                key={coin.symbol}
                value={`${coin.symbol} ${coin.name}`}
                onSelect={() => {
                  runCommand(() => {
                    router.push(`/terminal/futures/${coin.symbol}`)
                  })
                }}
                className="py-2 group"
              >
                {/*
                 * Layout (4 columns):
                 *   [logo + name/vol]  [price]  [change badge]  [★]
                 *
                 * Fixed right-side min-widths so columns never collapse
                 * even when market data hasn't loaded yet.
                 */}
                <div className="flex items-center gap-3 w-full">

                  {/* Logo — DiceBear identicon as fallback */}
                  <div className="w-7 h-7 rounded-full overflow-hidden shrink-0 border border-border/20 bg-muted/30">
                    {resolvedIcon ? (
                      <img
                        src={resolvedIcon}
                        alt={coin.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fall back to identicon on load error
                          e.currentTarget.src = identiconUrl(coin.symbol)
                        }}
                      />
                    ) : (
                      <img
                        src={identiconUrl(coin.symbol)}
                        alt={coin.name}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>

                  {/* Symbol (line 1) + name + 24h vol + venue logos (line 2) — flex-1 */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium leading-tight">{coin.symbol}<span className="text-muted-foreground font-normal">-USDC</span></p>
                      {coin.maxLeverage && (
                        <span className="text-[9px] px-1 py-0 bg-primary/10 text-primary font-semibold leading-relaxed shrink-0">{coin.maxLeverage}x</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <p className="text-[10px] text-muted-foreground leading-tight">
                        {coin.name}
                        {market ? ` · ${formatVolume(market.quoteVolume)}` : ""}
                      </p>
                      <VenueBadges venues={getVenuesForCoin(coin.symbol)} size="xs" />
                    </div>
                  </div>

                  {/* Price — always present, empty when no data */}
                  <div className="text-right shrink-0 min-w-[70px]">
                    {market && (
                      <p className="text-xs font-mono font-medium leading-tight">
                        {formatPrice(market.price)}
                      </p>
                    )}
                  </div>

                  {/* 24h change badge — always present, empty when no data */}
                  <div className="shrink-0 min-w-[58px] flex justify-end">
                    {market && (
                      <span
                        className={cn(
                          "text-[11px] px-1.5 py-0.5 rounded font-semibold font-mono",
                          isPositive
                            ? "bg-green-500/15 text-green-500"
                            : "bg-red-500/15 text-red-500"
                        )}
                      >
                        {isPositive ? "+" : ""}
                        {market.change.toFixed(2)}%
                      </span>
                    )}
                  </div>

                  {/* Watchlist star — fades in on hover */}
                  <button
                    className="p-0.5 rounded hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    onClick={(e) => {
                      e.stopPropagation()
                      toggleWatchlist(coin.symbol)
                    }}
                  >
                    <Star
                      className={cn(
                        "h-3 w-3",
                        isWatchlisted
                          ? "text-amber-400 fill-amber-400"
                          : "text-muted-foreground"
                      )}
                    />
                  </button>
                </div>
              </CommandItem>
            )
          })}
        </CommandGroup>

        {/* HL DEX TradFi assets — Stocks / Forex / Commodities / Indices */}
        {(["Stocks", "Forex", "Commodities", "Indices"] as const).map((tab) => {
          const coins = hlDexCoins.filter((c) => c.tab === tab)
          const hidden = activeFilter !== "All" && activeFilter !== tab
          return (
            <CommandGroup key={tab} heading={tab} className={hidden ? "hidden" : ""}>
              {coins.length === 0 && !hidden && (
                <CommandItem value={`loading-${tab}`} disabled className="py-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <div className="size-3 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin" />
                    <span className="text-sm">Loading {tab}…</span>
                  </div>
                </CommandItem>
              )}
              {coins.map((coin) => {
                const market = hlDexPriceMap.get(coin.symbol)
                const isPositive = (market?.change ?? 0) >= 0
                const isWatchlisted = watchlistSymbols.includes(coin.symbol)
                const price = market?.price ?? null
                const priceStr = price == null ? null :
                  price >= 10000
                    ? price.toLocaleString(undefined, { maximumFractionDigits: 0 })
                    : price >= 1
                    ? price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : price.toPrecision(4)
                return (
                  <CommandItem
                    key={coin.symbol}
                    value={`${coin.symbol} ${coin.name}`}
                    onSelect={() => {
                      runCommand(() => router.push(`/terminal/${hlDexUrlCategory(coin.tab)}/${coin.symbol}`))
                    }}
                    className="py-2 group"
                  >
                    <div className="flex items-center gap-3 w-full">
                      <RwaCmdIcon symbol={coin.symbol} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p className="text-xs font-medium leading-tight">{coin.symbol}</p>
                          {coin.maxLeverage && (
                            <span className="text-[9px] px-1 py-0 bg-primary/10 text-primary font-semibold leading-relaxed shrink-0">{coin.maxLeverage}x</span>
                          )}
                        </div>
                        <p className="text-[10px] text-muted-foreground leading-tight">{coin.name}</p>
                      </div>
                      <div className="text-right shrink-0 min-w-[70px]">
                        {priceStr && (
                          <p className="text-xs font-mono leading-tight">${priceStr}</p>
                        )}
                      </div>
                      <div className="shrink-0 min-w-[52px] flex justify-end">
                        {market && (
                          <span
                            className={cn(
                              "text-[11px] px-1.5 py-0.5 rounded font-semibold font-mono",
                              isPositive
                                ? "bg-green-500/15 text-green-500"
                                : "bg-red-500/15 text-red-500"
                            )}
                          >
                            {isPositive ? "+" : ""}{market.change.toFixed(2)}%
                          </span>
                        )}
                      </div>
                      <button
                        className="p-0.5 rounded hover:bg-muted opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleWatchlist(coin.symbol)
                        }}
                      >
                        <Star
                          className={cn(
                            "h-3 w-3",
                            isWatchlisted
                              ? "text-amber-400 fill-amber-400"
                              : "text-muted-foreground"
                          )}
                        />
                      </button>
                    </div>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          )
        })}

      </CommandList>
    </CommandDialog>
  )
}
